import { Head, Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"
import { useState } from "react"
import { Eye, EyeOff } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { AuthLayout } from "@/layouts/auth-layout"

interface LoginProps {
  flash?: { alert?: string; notice?: string }
}

function Login({ flash }: LoginProps) {
  const [showPassword, setShowPassword] = useState(false)
  const { data, setData, post, processing } = useForm({
    email_address: "",
    password: "",
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post("/session")
  }

  return (
    <>
      <Head title="Connexion" />
      <Card className="w-full pt-4">
        <CardHeader>
          <CardTitle className="text-center">Connexion</CardTitle>
          <CardDescription className="text-center">Entrez vos identifiants pour accéder à votre compte</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-2">
          <div className="min-h-5 text-sm text-center">
            {flash?.alert && <p className="text-destructive">{flash.alert}</p>}
            {flash?.notice && <p className="text-success">{flash.notice}</p>}
          </div>
          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email_address">Email</Label>
              <Input
                id="email_address"
                type="email"
                placeholder="vous@exemple.com"
                value={data.email_address}
                onChange={(e) => setData("email_address", e.target.value)}
                required
                autoFocus
                autoComplete="username"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={data.password}
                  onChange={(e) => setData("password", e.target.value)}
                  required
                  autoComplete="current-password"
                  maxLength={72}
                  className="pr-9"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 active:translate-y-[-50%]"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </Button>
              </div>
            </div>
            <Button type="submit" size="lg" disabled={processing} className="w-full">
              {processing ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="justify-center">
          <Link href="/passwords/new" className="text-sm text-muted-foreground hover:underline">
            Mot de passe oublié ?
          </Link>
        </CardFooter>
      </Card>
    </>
  )
}

Login.layout = (page: React.ReactNode) => <AuthLayout>{page}</AuthLayout>

export default Login
